import { useEffect, useMemo, useState } from 'react';

import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';

import { LINK_FARM, MARKETING_LINKS } from '../links';
import type { LinkFarmAppCategory, LinkFarmCategory } from '../types';
import type { LinkFarmLink } from '../types';

interface HookOptions {
  linkType: 'internal' | 'external' | 'all';
  category?: LinkFarmCategory | LinkFarmAppCategory;
}

function getLinkKey(link: LinkFarmLink): string {
  return `${link.appName}::${link.url}`;
}

export function useLinkFarmLinks({ linkType, category }: HookOptions) {
  const allFlagValues = useFeatureFlags(Object.values(FEATURE_FLAGS));

  // Stabilize reference: useFeatureFlags returns a new array every render,
  // but we only need to recalculate when the values actually change.
  const flagsKey = JSON.stringify(allFlagValues);
  const allFlags = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(FEATURE_FLAGS).map(([, key], index) => [
          key,
          allFlagValues[index],
        ]),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flagsKey],
  );

  const links = useMemo(() => {
    let targetLinks: LinkFarmLink[] = [];

    if (category) {
      // In the future, if we have other links that are outside LINK_FARM, we
      // can check for them here as well.
      if (category === 'marketing') {
        targetLinks = MARKETING_LINKS;
      } else {
        targetLinks = LINK_FARM[category] || [];
      }
    } else {
      // All possible links. In the future, if we have other links that are
      // outside LINK_FARM, then we can concat them here as well.
      targetLinks = Object.values(LINK_FARM).flat().concat(MARKETING_LINKS);
    }

    return targetLinks.filter((item) => {
      // Filter out any links that are feature flagged and have the flag
      // currently turned off.
      if (item.featureFlagKey && !allFlags[item.featureFlagKey]) {
        return false;
      }

      return (
        linkType === 'all' ||
        (linkType === 'internal' && item.isInternal) ||
        (linkType === 'external' && !item.isInternal)
      );
    });
  }, [allFlags, category, linkType]);

  const [accessResults, setAccessResults] = useState<Record<string, boolean>>(
    {},
  );

  useEffect(() => {
    const controller = new AbortController();

    links.forEach((link) => {
      if (!link.accessCheck) {
        return;
      }

      const key = getLinkKey(link);

      link
        .accessCheck(controller.signal)
        .then((allowed) => {
          if (!controller.signal.aborted) {
            setAccessResults((prev) => ({ ...prev, [key]: allowed }));
          }
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            setAccessResults((prev) => ({ ...prev, [key]: false }));
          }
        });
    });

    return () => {
      controller.abort();
    };
  }, [links]);

  return useMemo(
    () =>
      links.filter((link) => {
        if (!link.accessCheck) {
          return true;
        }

        return !!accessResults[getLinkKey(link)];
      }),
    [links, accessResults],
  );
}
